import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 9,
  },

  // Header bar
  headerBar: {
    backgroundColor: "#0047AB", // Deeper royal blue
    padding: 8,
    marginBottom: 20,
  },
  headerText: {
    color: "#FFFFFF",
    fontSize: 14,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
  },

  // Section titles
  sectionTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#0047AB", // Match header blue
    marginBottom: 8,
    marginTop: 16,
  },

  // Two-column layout for Award Overview
  overviewGrid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  overviewColumn: {
    width: "50%",
    marginBottom: 6,
  },
  label: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    marginBottom: 2,
  },
  value: {
    fontSize: 9,
    color: "#000000",
  },

  // Planning considerations
  bulletList: {
    marginTop: 8,
  },
  bulletItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  bullet: {
    width: 10,
    marginRight: 5,
  },
  bulletText: {
    flex: 1,
    fontSize: 9,
  },

  // Tables
  table: {
    width: "100%",
    marginTop: 8,
  },
  tableHeader: {
    backgroundColor: "#F0F0F0",
    borderBottom: 1,
    borderBottomColor: "#000000",
  },
  tableHeaderCell: {
    padding: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#000000",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#CCCCCC",
  },
  tableCell: {
    padding: 6,
    fontSize: 9,
    flex: 1,
  },
  tableCellWide: {
    padding: 6,
    fontSize: 9,
    flex: 2,
  },

  // Footer
  footer: {
    marginTop: 20,
    fontSize: 8,
    fontStyle: "italic",
    textAlign: "center",
    color: "#666666",
  },
});

const RSUSummaryPDF = ({ data }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.headerBar}>
        <Text style={styles.headerText}>RSU Tax Impact Summary</Text>
      </View>

      {/* Summary Question */}
      <View>
        <Text style={styles.sectionTitle}>Summary Question</Text>
        <Text style={styles.bulletText}>{data.question}</Text>
      </View>

      {/* Award Overview */}
      <View>
        <Text style={styles.sectionTitle}>Award Overview</Text>
        <View style={styles.overviewGrid}>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Awardee:</Text>
            <Text style={styles.value}>{data.awardee}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Employer:</Text>
            <Text style={styles.value}>{data.employer}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Position:</Text>
            <Text style={styles.value}>{data.position}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Grant Date:</Text>
            <Text style={styles.value}>{data.grantDate}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Total RSUs Granted:</Text>
            <Text style={styles.value}>{data.totalRSUs}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Vesting Schedule:</Text>
            <Text style={styles.value}>{data.vestingSchedule}</Text>
          </View>
          <View style={styles.overviewColumn}>
            <Text style={styles.label}>Performance Bonus:</Text>
            <Text style={styles.value}>{data.performanceBonus}</Text>
          </View>
        </View>
      </View>

      {/* Key Planning Considerations */}
      <View>
        <Text style={styles.sectionTitle}>Key Planning Considerations</Text>
        <View style={styles.bulletList}>
          {data.planningConsiderations.map((item, idx) => (
            <View key={idx} style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Taxation Timing & Treatment */}
      <View>
        <Text style={styles.sectionTitle}>Taxation Timing & Treatment</Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableHeaderCell}>Event</Text>
            <Text style={styles.tableHeaderCell}>When</Text>
            <Text style={styles.tableHeaderCell}>Tax Impact</Text>
            <Text style={styles.tableHeaderCell}>Tax Type</Text>
          </View>
          {data.taxationTable.map((row, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.tableCell}>{row.event}</Text>
              <Text style={styles.tableCell}>{row.when}</Text>
              <Text style={styles.tableCell}>{row.impact}</Text>
              <Text style={styles.tableCell}>{row.type}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Actionable Recommendations */}
      <View>
        <Text style={styles.sectionTitle}>Actionable Recommendations</Text>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableHeaderCell}>Item</Text>
            <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Action</Text>
          </View>
          {data.recommendations.map((rec, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.tableCell}>{rec.item}</Text>
              <Text style={styles.tableCellWide}>{rec.action}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Footer */}
      <Text style={styles.footer}>
        _Generated on {new Date().toLocaleDateString()} – For internal use only_
      </Text>
    </Page>
  </Document>
);

export default RSUSummaryPDF;
